import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Button, Grid, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useStationOrderSounds } from "../hooks/useStationOrderSounds";
import { useStationOrders } from "../hooks/useStationOrders";
import { StationOrderCard } from "./components/StationOrderCard";

export default function StationViewPage() {
  const navigate = useNavigate();
  const { stationOrders, doneStationOrder } = useStationOrders();
  const [params] = useSearchParams();
  const stationId = params.get("id");
  const [loadingId, setLoadingId] = useState<string | null>(null);
  useStationOrderSounds(stationOrders || []);

  const allStationOrders = useMemo(() => {
    if (!stationOrders) return [];
    return stationOrders
      .filter((s) => s.stationId === stationId)
      .sort((a, b) => a.createdAt - b.createdAt);
  }, [stationOrders, stationId]);

  const summaryByProduct = useMemo(() => {
    const map = new Map<
      string,
      { productId: string; name: string; totalQuantity: number }
    >();
    allStationOrders.forEach((order) => {
      order.items
        .filter((item) => item.status === "PENDING")
        .forEach((item) => {
          const key = item.productId;
          if (!map.has(key)) {
            map.set(key, {
              productId: item.productId,
              name: item.name,
              totalQuantity: item.quantity,
            });
          } else {
            map.get(key)!.totalQuantity += item.quantity;
          }
        });
    });
    return Array.from(map.values());
  }, [allStationOrders]);

  if (!stationId) {
    return <Typography color="error">No se envió ID de estación.</Typography>;
  }

  const handleBack = () => navigate("/dashboard/stations");

  return (
    <Box
      sx={{
        p: 3,
        height: "100vh",
        overflow: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{
              borderRadius: "12px",
              borderColor: "divider",
              color: "primary.main",
              whiteSpace: "nowrap",
              "&:hover": { borderColor: "secondary.main" },
            }}
          >
            Volver
          </Button>

          <Typography
            variant="h5"
            fontWeight={700}
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "250px",
            }}
          >
            {allStationOrders[0]?.stationName || "Estación"}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            whiteSpace: "nowrap",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "3px",
                bgcolor: "#2196f3",
              }}
            />
            <Typography variant="body2">MESAS</Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "3px",
                bgcolor: "#9c27b0",
              }}
            />
            <Typography variant="body2">DOMICILIOS</Typography>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={9.5}>
          <Grid container spacing={3}>
            {allStationOrders
              .filter((orderStation) =>
                orderStation.items.some((i) => i.status === "PENDING")
              )
              .map((orderStation, index) => (
                <Grid
                  key={`${orderStation.orderId}-${index}`}
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={3}
                >
                  <StationOrderCard
                    orderStation={orderStation}
                    loadingId={loadingId}
                    doneStationOrder={async (id: string) => {
                      setLoadingId(id);
                      await doneStationOrder(id);
                      setLoadingId(null);
                    }}
                  />
                </Grid>
              ))}
          </Grid>
        </Grid>
        <Grid item xs={12} md={2.5}>
          <Box
            sx={{
              position: "sticky",
              top: 16,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              p: 1.5,
              bgcolor: "background.paper",
              maxHeight: "calc(100vh - 120px)",
              overflowY: "auto",
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={700}
              mb={1}
              sx={{ fontSize: 14 }}
            >
              Resumen
            </Typography>
            {summaryByProduct.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                No hay productos pendientes.
              </Typography>
            ) : (
              summaryByProduct
                .sort((a, b) => b.totalQuantity - a.totalQuantity)
                .map((item) => (
                  <Box
                    key={item.productId}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      py: 0.5,
                      borderBottom: "1px dashed",
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        maxWidth: "75%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.name}
                    </Typography>
                    <Box
                      sx={{
                        minWidth: 32,
                        height: 22,
                        borderRadius: 1,
                        bgcolor: "success.main",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      x{item.totalQuantity}
                    </Box>
                  </Box>
                ))
            )}
          </Box>
        </Grid>
      </Grid>

      {allStationOrders.length === 0 && (
        <Typography textAlign="center" color="text.secondary" mt={5}>
          No hay comandas pendientes en esta estación.
        </Typography>
      )}
    </Box>
  );
}
